import { useState } from 'react';
import { createProfile } from '../api/pocketbase';
import { toPocketBaseDateTime } from '../utils/dateFields';

function ProfileForm({ onCreated }) {
  const [form, setForm] = useState({
    email: '',
    app_password: '',
    cnn: '',
    date_reserver: '',
    date_taken: '',
    id_pc: '',
    phone_number: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const getTodayStr = () => {
    const date = new Date();
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().split('T')[0];
  };
  const todayStr = getTodayStr();
  const isFutureDate = (dateValue) => {
    const normalized = dateValue ? dateValue.split(' ')[0].split('T')[0] : '';
    return normalized >= todayStr;
  };

  const validate = () => {
    const e = {};
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.app_password.trim()) e.app_password = 'Required';
    if (!form.cnn.trim()) e.cnn = 'Required';
    if (!form.date_reserver) e.date_reserver = 'Required';
    if (!form.phone_number.trim()) e.phone_number = 'Required';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (Object.keys(v).length > 0) { setErrors(v); return; }

    setSubmitting(true);
    try {
      const dateTaken = toPocketBaseDateTime(form.date_taken);
      const payload = {
        email: form.email.trim(),
        app_password: form.app_password.trim(),
        cnn: form.cnn.trim(),
        date_reserver: form.date_reserver,
        id_pc: isFutureDate(form.date_reserver) ? '-' : form.id_pc.trim() || '-',
        phone_number: form.phone_number.trim(),
      };

      if (dateTaken) payload.date_taken = dateTaken;

      await createProfile(payload);
      setForm({ email: '', app_password: '', cnn: '', date_reserver: '', date_taken: '', id_pc: '', phone_number: '' });
      onCreated();
    } catch (err) {
      setErrors({ email: err.message });
    }
    setSubmitting(false);
  };

  const inputCls = (field) =>
    `w-full px-3.5 py-2.5 rounded-lg border bg-white/[0.03] text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-all duration-150 ${
      errors[field]
        ? 'border-red-500/40 focus:border-red-500/60'
        : 'border-white/[0.08] focus:border-white/20 hover:border-white/12'
    } focus:bg-white/[0.04]`;

  return (
    <div className="border border-white/[0.06] rounded-xl bg-white/[0.02] overflow-hidden">
      <div className="px-6 py-5 border-b border-white/[0.06]">
        <h2 className="text-sm font-semibold text-white tracking-tight">Create Profile</h2>
        <p className="text-xs text-zinc-500 mt-1">Add a new profile to the database.</p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-2">Email</label>
            <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="name@example.com" className={inputCls('email')} />
            {errors.email && <p className="text-xs text-red-400 mt-1.5">{errors.email}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-2">App Password</label>
            <input name="app_password" value={form.app_password} onChange={handleChange} placeholder="Enter password" className={inputCls('app_password')} />
            {errors.app_password && <p className="text-xs text-red-400 mt-1.5">{errors.app_password}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-2">CNN</label>
            <input name="cnn" value={form.cnn} onChange={handleChange} placeholder="CNN number" className={inputCls('cnn')} />
            {errors.cnn && <p className="text-xs text-red-400 mt-1.5">{errors.cnn}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-2">Date Reserver</label>
            <input name="date_reserver" type="date" value={form.date_reserver} onChange={handleChange} className={inputCls('date_reserver')} />
            {errors.date_reserver && <p className="text-xs text-red-400 mt-1.5">{errors.date_reserver}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-2">Date Taken</label>
            <input name="date_taken" type="datetime-local" value={form.date_taken} onChange={handleChange} className={inputCls('date_taken')} />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-2">ID PC</label>
            <input name="id_pc" value={form.id_pc} onChange={handleChange} placeholder="PC identifier" className={inputCls('id_pc')} />
            {errors.id_pc && <p className="text-xs text-red-400 mt-1.5">{errors.id_pc}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-2">Phone Number</label>
            <input name="phone_number" value={form.phone_number} onChange={handleChange} placeholder="+212 600000000" className={inputCls('phone_number')} />
            {errors.phone_number && <p className="text-xs text-red-400 mt-1.5">{errors.phone_number}</p>}
          </div>
        </div>

        <div className="pt-3 border-t border-white/[0.06]">
          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-2.5 rounded-lg bg-white text-[#09090b] text-sm font-medium hover:bg-zinc-200 active:scale-[0.98] transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? 'Creating...' : 'Create Profile'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProfileForm;
